import type { Category, ContentTopic } from "api/config/chalice-types";
import { useMemo } from "react";

interface UseTopicOrganizationProps {
  topics: ContentTopic[];
  categories: Category[];
}

/**
 * Organizes the list of topics by category for display purposes.
 * It groups topics under their category name (including 'Uncategorized'),
 * calculates the total enabled topics count, and provides a sorted list of category names.
 */
export const useTopicOrganization = ({
  topics,
  categories,
}: UseTopicOrganizationProps) => {
  const enabledTopicsCount = useMemo(
    () => topics.filter(({ enabled }: ContentTopic) => enabled).length,
    [topics]
  );

  const { topicsByCategory, categoryIdToNameMap } = useMemo(() => {
    const catMap: Record<string, string> = {};
    categories.forEach((cat: Category) => {
      if (cat?.id) {
        catMap[cat.id] = cat.name ?? "Unnamed Category";
      }
    });
    const grouped: Record<string, ContentTopic[]> = { Uncategorized: [] };
    categories.forEach((cat: Category) => {
      if (cat?.name) {
        grouped[cat.name] = [];
      }
    });

    topics.forEach((topic: ContentTopic) => {
      const categoryName = topic.category_id ? catMap[topic.category_id] : null;
      const targetGroupName = categoryName || "Uncategorized";
      if (!grouped[targetGroupName]) {
        grouped[targetGroupName] = [];
      }
      grouped[targetGroupName].push(topic);
    });

    return { topicsByCategory: grouped, categoryIdToNameMap: catMap };
  }, [topics, categories]);

  const sortedCategoryNames = useMemo(() => {
    const cats = Object.keys(topicsByCategory);
    return cats.sort((a, b) => {
      if (a === "Uncategorized") return -1;
      if (b === "Uncategorized") return 1;
      return a.localeCompare(b);
    });
  }, [topicsByCategory]);

  return {
    enabledTopicsCount,
    topicsByCategory,
    categoryIdToNameMap, // Though unused currently, keep for potential future use
    sortedCategoryNames,
  };
};
